import { onBeforeUnmount, onMounted } from 'vue'
import type { ItemRow } from '~~/shared/items/itemRow'
import type { ResolvedDocumentGroup } from '~/composables/useDocumentGroups'
import { useDocumentsStore } from '~/stores/documents'

/**
 * Register the calling component as a file explorer for as long as it is mounted (MAR-989). Both
 * the sidebar and `/app/docs` call this, so "an explorer is on screen" means exactly "one of them is
 * mounted" — never a panel flag that one of them can be open without.
 */
export function useRegisteredFileExplorer(make: () => FileExplorerSurface) {
  const documents = useDocumentsStore()
  let unregister: (() => void) | null = null
  onMounted(() => { unregister = documents.registerFileExplorer(make()) })
  onBeforeUnmount(() => { unregister?.(); unregister = null })
}

/**
 * A file explorer that is on screen, as the chat sees it (MAR-989). Registered with the documents
 * store by the component that renders it, for as long as it is mounted — see `registerFileExplorer`.
 *
 * The three readers are all the chat needs: the rows the user is looking at, the text in the
 * surface's filter box, and the folder it is showing (null when it is not showing one). They are
 * functions, read at the moment the chat builds its ViewState, so they cannot go stale between turns.
 */
export interface FileExplorerSurface {
  /** `filter_files`: put `query` in this surface's own filter box and apply it ('' clears). */
  applyFilter: (query: string) => void
  /** The rows the surface is RENDERING, in display order. */
  rows: () => ItemRow[]
  /** The text in the surface's filter box, '' when none. */
  filter: () => string
  /** The folder being listed, or null when the surface is not showing a folder (grouped, searching). */
  folderPath: () => string | null
}

/** The store fields both surfaces read. Structural, so a test can hand over the real store. */
interface ExplorerStoreView {
  isFolderMode: boolean
  searchQuery: string
  searchResults: ItemRow[]
  items: ItemRow[]
  currentPath: string
  groupFilter: string
  flatVisibleItems: ItemRow[]
}

/**
 * What a FOLDER listing shows: the search hits while a search is running, the folder otherwise.
 *
 * Both surfaces render this in folder mode. The sidebar used to render `items` whatever the box
 * said, so in "Árbol de carpetas" a search — typed, or set by `filter_files` — moved nothing on
 * screen while the tool reported success (CHAT-2.01). One definition, so the list and the agent's
 * account of it cannot disagree.
 */
export function folderListingRows(d: Pick<ExplorerStoreView, 'searchQuery' | 'searchResults' | 'items'>): ItemRow[] {
  return d.searchQuery.trim() ? d.searchResults : d.items
}

/** The sidebar drawer (`FileExplorer`). Grouped modes filter client-side (`groupFilter`) and
 *  publish their flattened rows as `flatVisibleItems`; folder mode runs the server search. */
export function sidebarExplorerSurface(d: ExplorerStoreView, applyFilter: (q: string) => void): FileExplorerSurface {
  return {
    applyFilter,
    rows: () => (d.isFolderMode ? folderListingRows(d) : d.flatVisibleItems),
    filter: () => (d.isFolderMode ? d.searchQuery : d.groupFilter),
    folderPath: () => (d.isFolderMode && d.currentPath ? d.currentPath : null),
  }
}

/** The full-page finder (`/app/docs`). A search is flat in every mode; without one, folder mode
 *  lists the folder and a grouped mode lists its groups' documents, nested matters included. */
export function finderExplorerSurface(
  d: ExplorerStoreView,
  groups: () => ResolvedDocumentGroup[],
  applyFilter: (q: string) => void,
): FileExplorerSurface {
  return {
    applyFilter,
    rows: () => {
      if (d.isFolderMode || d.searchQuery.trim()) return folderListingRows(d)
      return groups().flatMap(g => [...g.documents, ...(g.subgroups?.flatMap(s => s.documents) ?? [])])
    },
    filter: () => d.searchQuery,
    folderPath: () => (d.isFolderMode && d.currentPath ? d.currentPath : null),
  }
}
